import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../theme/colors";
import { formatDistanceToNow } from "date-fns";
import { useUser } from "../../context/UserContext";
import { 
  getPostComments, 
  createPostComment, 
  updatePostComment, 
  deletePostComment, 
  likePostComment, 
  unlikePostComment,
  reportPostComment,
  acceptCommunityGuidelines,
} from "../../utils/feedApi";
import { Link as RouterLink } from "react-router-dom";
import ReportModal from "../AlertComponents/ReportModal";
import CommunityGuidelinesModal from "./CommunityGuidelinesModal";
import { isPoliticianAccount, canPostAnonymously } from "../../utils/accountTypes";
import AnonymousToggle from "./AnonymousToggle";
import IncognitoAvatar from "./IncognitoAvatar";

function countAllComments(list) {
  return list.reduce(
    (n, c) => n + 1 + (c.replies ? countAllComments(c.replies) : 0),
    0
  );
}

function countVerifiedComments(list) {
  return list.reduce(
    (n, c) => n + ((c.author_is_verified ?? c.user?.is_verified) ? 1 : 0) + (c.replies ? countVerifiedComments(c.replies) : 0),
    0
  );
}

function CommentItem({ 
  comment, 
  currentUserId,
  currentUserImage,
  postId, 
  onReply, 
  onDelete, 
  onUpdate,
  onReport,
  depth = 0,
  replyingTo,
  replyContent,
  onReplyContentChange,
  onSubmitReply,
  onCancelReply,
  submitting,
  canPostComment = true,
  verifiedCountsOnly = false,
  viewerIsVerified = false,
  onAuthRequired,
  canChooseAnonymous = false,
  anonymous = false,
  onAnonymousChange,
  submitError,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isLiking, setIsLiking] = useState(false);
  const [hasLiked, setHasLiked] = useState(comment.viewer_has_liked ?? comment.has_liked);
  const baseLikeCount = verifiedCountsOnly
    ? (comment.verified_like_count ?? 0)
    : (comment.like_count || 0);
  const [likeCount, setLikeCount] = useState(baseLikeCount);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setLikeCount(
      verifiedCountsOnly
        ? (comment.verified_like_count ?? 0)
        : (comment.like_count || 0)
    );
  }, [comment.like_count, comment.verified_like_count, verifiedCountsOnly]);

  const isLoggedIn = !!currentUserId;
  const isAuthor = comment.viewer_is_author ?? (isLoggedIn && comment.user?.id === currentUserId);
  const showOverflowMenu = isAuthor || isLoggedIn;
  const profileUserId = comment.anonymous ? null : comment.user?.id;
  const authorName = comment.anonymous
    ? "Anonymous"
    : comment.user?.entity_name || `${comment.user?.first_name || ""} ${comment.user?.last_name || ""}`.trim() || "Anonymous";
  const displayName = comment.anonymous && isAuthor ? "Anonymous (you)" : authorName;
  const timeAgo = comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }) : "";

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLikeToggle = async () => {
    if (!canPostComment) {
      onAuthRequired?.();
      return;
    }
    if (isLiking) return;
    try {
      setIsLiking(true);
      const shouldAdjustVerifiedCount = !verifiedCountsOnly || viewerIsVerified;
      setHasLiked(!hasLiked);
      if (shouldAdjustVerifiedCount) {
        setLikeCount(prev => hasLiked ? Math.max(0, prev - 1) : prev + 1);
      }

      if (hasLiked) {
        await unlikePostComment(postId, comment.id);
      } else {
        await likePostComment(postId, comment.id);
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      setHasLiked(hasLiked);
      setLikeCount(
        verifiedCountsOnly
          ? (comment.verified_like_count ?? 0)
          : (comment.like_count || 0)
      );
    } finally {
      setIsLiking(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim() || editContent === comment.content) {
      setIsEditing(false);
      return;
    }
    try {
      await onUpdate(comment.id, editContent);
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating comment:", err);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2, ml: depth * 4 }}>
      {comment.anonymous ? (
        <IncognitoAvatar size={36} verified={!!comment.author_is_verified} />
      ) : profileUserId ? (
        <Avatar
          component={RouterLink}
          to={`/profile/${profileUserId}`}
          onClick={(e) => e.stopPropagation()}
          src={comment.user?.profile_image}
          sx={{
            width: 36,
            height: 36,
            bgcolor: `${theme.primary}20`,
            color: theme.primary,
            fontSize: "0.875rem",
            fontWeight: 700,
            textDecoration: "none",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {authorName.charAt(0).toUpperCase()}
        </Avatar>
      ) : (
        <Avatar src={comment.user?.profile_image} sx={{ width: 36, height: 36, bgcolor: `${theme.primary}20`, color: theme.primary, fontSize: "0.875rem", fontWeight: 700 }}>
          {authorName.charAt(0).toUpperCase()}
        </Avatar>
      )}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ bgcolor: "#f8fafc", p: 2, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Box>
              {profileUserId ? (
                <Typography
                  component={RouterLink}
                  to={`/profile/${profileUserId}`}
                  onClick={(e) => e.stopPropagation()}
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    textDecoration: "none",
                    display: "block",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {displayName}
                </Typography>
              ) : (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827" }}>
                  {displayName}
                </Typography>
              )}
              <Typography sx={{ color: "#111827", fontSize: "12px" }}>
                {timeAgo}
              </Typography>
            </Box>
            {showOverflowMenu && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-label="Comment actions"
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {isEditing ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                size="small"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                <Button size="small" onClick={() => { setIsEditing(false); setEditContent(comment.content); }}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveEdit} sx={{
                  background: theme.gradient,
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { background: theme.gradient, opacity: 0.92, color: '#fff' },
                }}>Save</Button>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: "#111827", whiteSpace: "pre-wrap", fontSize: "14px", lineHeight: 1.5 }}>
              {comment.content}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 0.5, ml: 1 }}>
          <Button
            variant="text"
            size="small"
            startIcon={hasLiked ? <ThumbUpIcon sx={{ fontSize: 14 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={handleLikeToggle}
            disabled={isLiking || isAuthor}
            sx={{
              color: hasLiked ? theme.primary : "#111827",
              minWidth: 0,
              p: 0,
              fontSize: "0.75rem",
              textTransform: "none",
              fontWeight: hasLiked ? 600 : 400,
              bgcolor: "transparent",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              "& .MuiButton-startIcon svg": { color: "inherit" },
            }}
          >
            {likeCount > 0 ? likeCount : "Like"}
          </Button>
          {(comment.can_reply === true || (comment.can_reply == null && depth < 1)) && (
            <Button
              variant="text"
              size="small"
              startIcon={<ReplyIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                if (!canPostComment) {
                  onAuthRequired?.();
                  return;
                }
                onReply(comment);
              }}
              sx={{
                color: "#111827",
                minWidth: 0,
                p: 0,
                fontSize: "0.75rem",
                textTransform: "none",
                bgcolor: "transparent",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                "& .MuiButton-startIcon svg": { color: "inherit" },
              }}
            >
              Reply
            </Button>
          )}
        </Box>

        {/* Inline reply form when this comment is the one being replied to */}
        {replyingTo?.id === comment.id && (
          <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Avatar src={currentUserImage} sx={{ width: 36, height: 36, bgcolor: `${theme.primary}20`, color: theme.primary, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, p: 1.25, bgcolor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <Typography sx={{ color: "#111827", fontSize: "13px", fontWeight: 500 }}>
                  Replying to <strong>{authorName}</strong>
                </Typography>
                <IconButton size="small" onClick={onCancelReply}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => onReplyContentChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    bgcolor: "#fff",
                    fontSize: "14px",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: theme.primary, borderWidth: "2px" },
                  },
                }}
              />
              {submitError && (
                <Typography role="alert" sx={{ color: "#b91c1c", fontSize: "13px", mt: 1 }}>
                  {submitError}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: canChooseAnonymous ? "space-between" : "flex-end", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {canChooseAnonymous && (
                  <AnonymousToggle
                    compact
                    label="Reply anonymously"
                    checked={anonymous}
                    onChange={onAnonymousChange}
                    disabled={submitting}
                  />
                )}
                <Button
                  variant="contained"
                  disabled={!canPostComment || !replyContent.trim() || submitting}
                  onClick={onSubmitReply}
                  sx={{
                    background: theme.gradient,
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    px: 2.5,
                    py: 1,
                    boxShadow: 'none',
                    '&:hover': { background: theme.gradient, opacity: 0.92, color: '#fff' },
                    '&.Mui-disabled': { background: '#e5e7eb', color: '#94a3b8' },
                  }}
                >
                  {submitting ? <CircularProgress size={20} color="inherit" /> : "Post Comment"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Render child comments */}
        {comment.replies && comment.replies.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {comment.replies.map(reply => (
              <CommentItem 
                key={reply.id} 
                comment={reply} 
                currentUserId={currentUserId}
                currentUserImage={currentUserImage}
                postId={postId}
                onReply={onReply}
                onDelete={onDelete}
                onUpdate={onUpdate}
                onReport={onReport}
                depth={depth + 1}
                replyingTo={replyingTo}
                replyContent={replyContent}
                onReplyContentChange={onReplyContentChange}
                onSubmitReply={onSubmitReply}
                onCancelReply={onCancelReply}
                submitting={submitting}
                canPostComment={canPostComment}
                verifiedCountsOnly={verifiedCountsOnly}
                viewerIsVerified={viewerIsVerified}
                onAuthRequired={onAuthRequired}
                canChooseAnonymous={canChooseAnonymous}
                anonymous={anonymous}
                onAnonymousChange={onAnonymousChange}
                submitError={submitError}
              />
            ))}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isAuthor ? [
          <MenuItem key="edit" onClick={() => { handleMenuClose(); setIsEditing(true); }}>Edit</MenuItem>,
          <MenuItem key="delete" onClick={() => { handleMenuClose(); onDelete(comment.id); }} sx={{ color: "error.main" }}>Delete</MenuItem>
        ] : isLoggedIn ? [
          <MenuItem key="report" onClick={() => { handleMenuClose(); onReport(comment); }} sx={{ color: "error.main" }}>Report</MenuItem>
        ] : []}
      </Menu>
    </Box>
  );
}

export default function PostCommentsSection({ postId, currentUserId, verifiedCountsOnly = false, onCommentCountChange, onAuthRequired }) {
  const {
    profileImage,
    user,
    verifiedUser,
    hasAcceptedCommunityGuidelines,
    setHasAcceptedCommunityGuidelines,
    loadUserContext,
    accountTypeName,
  } = useUser() || {};
  const isLoggedIn = !!user;
  const viewerIsVerified = typeof verifiedUser === "boolean" ? verifiedUser : false;
  const isPolitician = isPoliticianAccount(accountTypeName);
  const canPostComment = isLoggedIn;
  const canChooseAnonymous = canPostAnonymously(accountTypeName);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [commentAnonymous, setCommentAnonymous] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [commentToReport, setCommentToReport] = useState(null);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [pendingCommentAction, setPendingCommentAction] = useState(null);

  const onCommentCountChangeRef = useRef(onCommentCountChange);
  onCommentCountChangeRef.current = onCommentCountChange;

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPostComments(postId);
      const list = Array.isArray(data) ? data : data.results || [];
      setComments(list);
      const cb = onCommentCountChangeRef.current;
      if (cb) cb(countAllComments(list), countVerifiedComments(list));
    } catch (err) {
      console.error("Failed to fetch comments", err);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (postId) {
      fetchComments();
    }
  }, [postId, fetchComments]);

  const submitComment = async (content, parentCommentId = null, anonymous = false) => {
    setSubmitError(null);
    await createPostComment(postId, content, parentCommentId, anonymous);
    setNewComment("");
    setCommentAnonymous(false);
    setReplyingTo(null);
    await fetchComments();
  };

  const showSubmitError = (err) => {
    if (err?.response?.data?.code === "ANONYMOUS_NOT_ALLOWED_FOR_ACCOUNT_TYPE") {
      setSubmitError(err.response.data.error);
    } else {
      setSubmitError("Failed to post comment. Please try again.");
    }
  };

  const resetComposer = () => {
    setReplyingTo(null);
    setNewComment("");
    setCommentAnonymous(false);
    setSubmitError(null);
  };

  const handleAcceptGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      await acceptCommunityGuidelines();
      if (setHasAcceptedCommunityGuidelines) {
        setHasAcceptedCommunityGuidelines(true);
      }
      if (loadUserContext) {
        await loadUserContext();
      }
      setShowGuidelinesModal(false);
      if (pendingCommentAction) {
        const { content, parentCommentId, anonymous } = pendingCommentAction;
        setPendingCommentAction(null);
        setSubmitting(true);
        try {
          await submitComment(content, parentCommentId, anonymous);
        } catch (err) {
          console.error("Failed to create comment after accepting guidelines", err);
          showSubmitError(err);
        } finally {
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error accepting guidelines:", err);
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!canPostComment || !newComment.trim()) return;

    const content = newComment.trim();
    const parentCommentId = replyingTo?.id ?? null;
    const anonymous = canChooseAnonymous && commentAnonymous;

    if (!hasAcceptedCommunityGuidelines) {
      setPendingCommentAction({ content, parentCommentId, anonymous });
      setShowGuidelinesModal(true);
      return;
    }

    try {
      setSubmitting(true);
      await submitComment(content, parentCommentId, anonymous);
    } catch (err) {
      console.error("Failed to create comment", err);
      if (err?.response?.data?.code === "GUIDELINES_NOT_ACCEPTED") {
        setPendingCommentAction({ content, parentCommentId, anonymous });
        setShowGuidelinesModal(true);
      } else {
        showSubmitError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await deletePostComment(postId, commentId);
      await fetchComments();
    } catch (err) {
      console.error("Failed to delete comment", err);
    }
  };

  const handleUpdateComment = async (commentId, content) => {
    try {
      await updatePostComment(postId, commentId, content);
      await fetchComments();
    } catch (err) {
      console.error("Failed to update comment", err);
    }
  };

  const handleReportComment = (comment) => {
    if (!isLoggedIn) return;
    setCommentToReport(comment);
  };

  const handleReportSubmit = async (reason, details) => {
    if (!commentToReport) return;
    await reportPostComment(postId, commentToReport.id, reason, details);
    setCommentToReport(null);
  };

  return (
    <Box>
      {/* Top-level comment input: only when not replying to a specific comment */}
      {!isLoggedIn && !replyingTo && (
        <Box
          component="button"
          type="button"
          onClick={() => onAuthRequired?.()}
          sx={{
            mb: 1.5,
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 0,
            border: "none",
            bgcolor: "transparent",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <Box
            sx={{
              flex: 1,
              bgcolor: "#e2e8f0",
              borderRadius: "20px",
              px: 2,
              py: 1.25,
            }}
          >
            <Typography sx={{ fontSize: "14px", color: "#64748b" }}>
              Sign in to comment
            </Typography>
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "20px",
              bgcolor: theme.primary,
              opacity: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "#fff", fontSize: "18px", lineHeight: 1 }}>→</Typography>
          </Box>
        </Box>
      )}
      {isLoggedIn && !replyingTo && (
        <Box sx={{ mb: 1.5, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <Avatar src={profileImage} sx={{ width: 40, height: 40, bgcolor: `${theme.primary}20`, color: theme.primary }} />
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  fontSize: "14px",
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                  "&.Mui-focused fieldset": { borderColor: theme.primary, borderWidth: "2px" },
                },
              }}
            />
            {submitError && (
              <Typography role="alert" sx={{ color: "#b91c1c", fontSize: "13px", mt: 1 }}>
                {submitError}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: canChooseAnonymous ? "space-between" : "flex-end", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {canChooseAnonymous && (
                <AnonymousToggle
                  compact
                  label="Comment anonymously"
                  checked={commentAnonymous}
                  onChange={setCommentAnonymous}
                  disabled={submitting}
                />
              )}
              <Button
                variant="contained"
                disabled={!newComment.trim() || submitting}
                onClick={handleSubmitComment}
                sx={{
                  background: theme.gradient,
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  px: 2.5,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': { background: theme.gradient, opacity: 0.92, color: '#fff' },
                  '&.Mui-disabled': { background: '#e5e7eb', color: '#94a3b8' },
                }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : "Post Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 1, borderColor: "#e2e8f0" }} />

      {/* Comments List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress sx={{ color: theme.primary }} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#64748b", textAlign: "center", py: 2.5, fontSize: "14px" }}>
          {isLoggedIn
            ? "No comments yet. Be the first to share your thoughts!"
            : "No comments yet. Sign in to join the discussion."}
        </Typography>
      ) : (
        <Box>
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              currentUserImage={profileImage}
              postId={postId}
              onReply={(comment) => {
                if (!isLoggedIn) {
                  onAuthRequired?.();
                  return;
                }
                setReplyingTo(comment);
                setCommentAnonymous(false);
                setSubmitError(null);
              }}
              onAuthRequired={onAuthRequired}
              onDelete={handleDeleteComment}
              onUpdate={handleUpdateComment}
              onReport={handleReportComment}
              replyingTo={replyingTo}
              replyContent={newComment}
              onReplyContentChange={setNewComment}
              onSubmitReply={handleSubmitComment}
              onCancelReply={resetComposer}
              submitting={submitting}
              canPostComment={canPostComment}
              verifiedCountsOnly={verifiedCountsOnly}
              viewerIsVerified={viewerIsVerified}
              canChooseAnonymous={canChooseAnonymous}
              anonymous={commentAnonymous}
              onAnonymousChange={setCommentAnonymous}
              submitError={submitError}
            />
          ))}
        </Box>
      )}

      <ReportModal
        open={!!commentToReport}
        contentType="comment"
        onClose={() => setCommentToReport(null)}
        onSubmit={handleReportSubmit}
      />

      <CommunityGuidelinesModal
        open={showGuidelinesModal}
        onClose={() => {
          setShowGuidelinesModal(false);
          setPendingCommentAction(null);
        }}
        onAccept={handleAcceptGuidelines}
        loading={guidelinesLoading}
        isPolitician={isPolitician}
      />
    </Box>
  );
}